import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import DuplicatesTable from '../components/DuplicatesTable';
import DuplicatesModal from '../components/DuplicatesModal';

const getDateParams = (range) => {
  let startDate, endDate;
  const now = new Date();

  switch(range) {
    case '24hours':
      startDate = new Date(now);
      startDate.setHours(now.getHours() - 1);
      break;
    case '7days':
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      break;
    case 'month':
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
      break;
    case 'quarter':
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 3);
      break;
    case 'year':
      startDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    case 'all':
      startDate = null;
      endDate = null;
      break;
    default:
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
  }

  return {
    startDate: startDate ? startDate.toISOString().split('T')[0] : null,
    endDate: endDate ? endDate.toISOString().split('T')[0] : null,
  };
};

const buildDateQuery = (range) => {
  const { startDate, endDate } = getDateParams(range);
  if (!startDate) return '';
  return `?startDate=${startDate}${endDate ? `&endDate=${endDate}` : ''}`;
};

const AdminDuplicates = ({ fetchWithAuth, selectedProject }) => {
  const { t } = useTranslation();
  const [duplicates, setDuplicates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDuplicates, setUserDuplicates] = useState([]);
  const [dateRange, setDateRange] = useState('7days');

  const fetchDuplicates = useCallback(async (range) => {
    const currentRange = range || dateRange;
    const url = `${API_BASE_URL}/api/settings/duplicates/project/${selectedProject}${buildDateQuery(currentRange)}`;

    const response = await fetchWithAuth(url);
    const data = await response.json();
    setDuplicates(data);
  }, [selectedProject, fetchWithAuth]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    fetchDuplicates(range);
  };

  const fetchUserDuplicates = useCallback(async (username) => {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/settings/duplicates/project/${selectedProject}/username/${encodeURIComponent(username)}${buildDateQuery(dateRange)}`
    );
    const data = await response.json();
    setUserDuplicates(data);
  }, [selectedProject, dateRange, fetchWithAuth]);

  useEffect(() => {
    if (!selectedProject) return;
    fetchDuplicates(dateRange);
  }, [selectedProject, fetchDuplicates]);

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    await fetchUserDuplicates(user.username);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setUserDuplicates([]);
  };

  return (
    <div className="container mx-auto">
      {!selectedProject ? (
        <div className="text-center py-12 px-4 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-8">{t('Duplicates Detector™')}</h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="text-blue-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">{t('Analysis by user')}</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('View the number of duplicate photos per user and access the details of the images concerned for each user.')}
              </p>
            </div>

            <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="text-green-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">{t('Duplicates visualization')}</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('Examine duplicate photos in a gallery-like interface for easy identification and management.')}
              </p>
            </div>
          </div>

          <p className="text-gray-500 dark:text-gray-400 italic">
            {t('Select a project from the dropdown above to start analyzing duplicates')}
          </p>
        </div>
      ) : (
        <>
          {selectedProject && (
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {t('Detected duplicates')}
              </h2>
              <select
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 text-sm font-medium text-gray-700 dark:text-gray-200"
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
              >
                <option value="24hours">{t('Last 24 hours')}</option>
                <option value="7days">{t('Last 7 days')}</option>
                <option value="month">{t('Last month')}</option>
                <option value="quarter">{t('Last quarter')}</option>
                <option value="year">{t('Last year')}</option>
                <option value="all">{t('All time')}</option>
              </select>
            </div>
          )}

          {duplicates.length === 0 ? (
            <div className="text-center py-12 px-4 max-w-3xl mx-auto">
              <div className="mb-8">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">{t('No duplicates detected')}</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {t('No duplicate photos were found in this project. Photos are considered duplicates when they share the same CRC and are posted in two different forms.')}
              </p>
              <div className="p-4 bg-blue-50 rounded-lg inline-block">
                <p className="text-blue-800 text-sm">
                  {t('The system automatically checks for new duplicates every hour.')}
                </p>
              </div>
            </div>
          ) : (
            <DuplicatesTable
              duplicates={duplicates}
              onUserClick={handleUserClick}
            />
          )}
        </>
      )}

      {isModalOpen && (
        <DuplicatesModal
          isOpen={true}
          onClose={handleModalClose}
          username={selectedUser.username}
          duplicates={userDuplicates}
        />
      )}
    </div>
  );
};

export default AdminDuplicates;
